// src/pages/PerformanceAssessments/PerformanceAssessmentModal.js
import React, { useEffect, useState } from 'react';
import {
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CForm,
    CFormSelect,
    CFormTextarea,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableHeaderCell,
    CTableDataCell,
    CTableRow,
    CTooltip,
} from '@coreui/react';

import { apiClient } from 'services/Api';
import {
    SCORE_OPTIONS,
    ROLE_LABELS,
    getGroupsForRole,
    buildInitialForm,
} from './questions';

const SCORE_GUIDE_ROWS = [
    {
        label: '1 - Claramente muito abaixo das expectativas',
        description:
            'O desempenho é inadequado e inferior aos padrões de desempenho exigidos para a função. Um desempenho deste nível não pode manter-se.',
    },
    {
        label: '2 - Necessita de Melhorias',
        description:
            'O desempenho não atinge, de forma consistente, os padrões de desempenho para a função. É necessário que sejam efetuados esforços sérios para melhorar o desempenho.',
    },
    {
        label: '3 - Desempenho Satisfatório',
        description:
            'O desempenho cumpre, de forma regular, os padrões de desempenho requeridos para a função.',
    },
    {
        label: '4 - Bom',
        description:
            'O desempenho está além das expectativas, revela uma grande qualidade e uma atuação proativa.',
    },
    {
        label: '5 - Excecional',
        description:
            'O desempenho excede largamente as expectativas e de forma consistente. Destaca-se significativamente do conjunto de colaboradores, da sua área funcional. Contribui significativamente para a melhoria da qualidade do serviço prestado e dos resultados alcançados.',
    },
];

const PerformanceAssessmentModal = ({ visible, onClose, assessment, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(buildInitialForm(assessment || {}));

    useEffect(() => {
        if (assessment) {
            setForm(buildInitialForm(assessment));
            setError(null);
        }
    }, [assessment, visible]);

    if (!assessment) return null;

    const {
        department_name,
        evaluatee_name,
        evaluator_name,
        year,
        role,
        concluded,
        c1,
        c2,
        c3,
        c4,
    } = assessment;

    const finalScore = [c1, c2, c3, c4].reduce((sum, v) => sum + (Number(v) || 0), 0).toFixed(2);

    const groups = getGroupsForRole(role);
    const isReadOnly = !!concluded;

    const onChangeScore = (field) => (e) => {
        if (isReadOnly) return;
        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
        setForm((prev) => ({ ...prev, [field]: v }));
    };

    const onChangeObs = (e) => {
        if (isReadOnly) return;
        setForm((prev) => ({ ...prev, observations: e.target.value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (isReadOnly) {
            onClose();
            return;
        }
        setSaving(true);
        setError(null);

        try {
            const payload = {};
            Object.keys(form).forEach((key) => {
                const val = form[key];
                if (val === '') {
                    payload[key] = null;
                } else {
                    payload[key] = val;
                }
            });

            await apiClient.post(`/performance-assessments/${assessment.id}/`, payload);
            onSaved?.();
            onClose();
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                err?.message ||
                'Erro ao guardar a avaliação.';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <CModal
            visible={visible}
            onClose={onClose}
            alignment="center"
            size="xl"
            backdrop="static"
            scrollable
        >
            <CModalHeader>
                <CModalTitle>
                    Avaliação de Desempenho – {evaluatee_name} ({department_name}, {year})
                </CModalTitle>
            </CModalHeader>

            <CForm onSubmit={onSubmit}>
                <CModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <CRow className="mb-3">
                        <CCol>
                            <CTooltip
                                placement="right"
                                className="pa-score-tooltip"
                                content={
                                    <div className="text-start">
                                        {SCORE_GUIDE_ROWS.map((row, index) => (
                                            <div
                                                key={row.label}
                                                className={index === SCORE_GUIDE_ROWS.length - 1 ? '' : 'mb-3'}
                                            >
                                                <strong>{row.label}:</strong> {row.description}
                                            </div>
                                        ))}
                                    </div>
                                }
                            >
                                <span
                                    className="small text-body-secondary"
                                    style={{ cursor: 'help', textDecoration: 'underline dotted' }}
                                >
                                    Escala de classificação (passar o rato para ver)
                                </span>
                            </CTooltip>
                        </CCol>
                    </CRow>

                    {error && (
                        <CAlert color="danger" className="mb-3">
                            {error}
                        </CAlert>
                    )}

                    <CRow className="mb-3">
                        <CCol md={6}>
                            <strong>Função:</strong> {ROLE_LABELS[role] || role}
                        </CCol>
                        <CCol md={6} className="text-end">
                            <strong>Estado:</strong>{' '}
                            {concluded ? (
                                <CBadge color="success">Concluída</CBadge>
                            ) : (
                                <CBadge color="secondary">Em aberto</CBadge>
                            )}
                        </CCol>
                    </CRow>

                    {(c1 || c2 || c3 || c4) && (
                        <CRow className="mb-3">
                            <CCol md={2}>
                                <strong>C1:</strong> {c1 ?? '—'}
                            </CCol>
                            <CCol md={2}>
                                <strong>C2:</strong> {c2 ?? '—'}
                            </CCol>
                            <CCol md={2}>
                                <strong>C3:</strong> {c3 ?? '—'}
                            </CCol>
                            <CCol md={2}>
                                <strong>C4:</strong> {c4 ?? '—'}
                            </CCol>
                            <CCol md={4} className="text-end">
                                <strong>Nota final:</strong> {finalScore}
                            </CCol>
                        </CRow>
                    )}

                    {groups.map((group) => (
                        <CCard className="mb-4" key={group.key}>
                            <CCardHeader>
                                <h5 className="mb-0">{group.title}</h5>
                            </CCardHeader>

                            <CCardBody>
                                <CTable responsive hover>
                                    <CTableBody>
                                        {group.questions.map((qText, index) => {
                                            const fieldName = `${group.key}_${index + 1}`;
                                            const currentValue = form[fieldName] ?? '';

                                            return (
                                                <CTableRow key={fieldName}>
                                                    <CTableHeaderCell className="col-10">
                                                        <label htmlFor={`pa-${fieldName}`} className="fw-normal">
                                                            {qText}
                                                        </label>
                                                    </CTableHeaderCell>
                                                    <CTableDataCell className="col-2 text-center">
                                                        <CFormSelect
                                                            id={`pa-${fieldName}`}
                                                            value={currentValue}
                                                            onChange={onChangeScore(fieldName)}
                                                            disabled={isReadOnly}
                                                        >
                                                            <option value="">—</option>
                                                            {SCORE_OPTIONS.map((val) => (
                                                                <option key={`${fieldName}-${val}`} value={val}>
                                                                    {val}
                                                                </option>
                                                            ))}
                                                        </CFormSelect>
                                                    </CTableDataCell>
                                                </CTableRow>
                                            );
                                        })}
                                    </CTableBody>
                                </CTable>
                            </CCardBody>
                        </CCard>
                    ))}

                    <CRow className="mb-3">
                        <CCol>
                            <label className="form-label fw-bold">Observações</label>
                            <CFormTextarea
                                rows={3}
                                value={form.observations || ''}
                                onChange={onChangeObs}
                                disabled={isReadOnly}
                            />
                        </CCol>
                    </CRow>
                </CModalBody>

                <CModalFooter>
                    <CButton color="secondary" variant="ghost" onClick={onClose} disabled={saving}>
                        Fechar
                    </CButton>
                    <CButton
                        color="success"
                        type="submit"
                        disabled={saving || isReadOnly}
                    >
                        {isReadOnly ? 'Avaliação concluída' : saving ? <CSpinner size="sm" /> : 'Guardar'}
                    </CButton>
                </CModalFooter>
            </CForm>
        </CModal>
    );
};

export default PerformanceAssessmentModal;
